<script lang="ts" setup>
import Vue, { onMounted, ref } from 'vue';
import Suggest from '@/corePagesModule/components/Suggest.vue';
import { computed } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { MAX_OF_ADJUST_ANNOUNCE_RATE, MIN_OF_ADJUST_ANNOUNCE_RATE, STEP_OF_ADJUST_ANNOUNCE_RATE } from '@/coreDomainsModule/constants';
import { watch } from 'vue';
import { useSelectableNumberInputStore } from '../store/useSelectableNumberInputStore';
import { SelectableNumberInputOptions } from '@/coreDomainsModule/entities/inputOptions';

const props = defineProps<{
  displayValue: string;
  defaultValue: number;
  options: SelectableNumberInputOptions[];
}>();

const selectableNumberInputStore = useSelectableNumberInputStore();
const { state, updateInputValue, updateBySelectedSuggest, hoverSuggests, leaveSuggests, selectInput, leaveInput } =
  selectableNumberInputStore;

const result = ref<DOMRect>();
const xLength = ref(0);
const yLength = ref(0);

const emits = defineEmits<{
  (e: 'confirmed', value: number): void;
}>();

watch(
  () => props.defaultValue,
  (newValue) => {
    updateInputValue(newValue);
  },
  { immediate: true } // ないと初回が反映されないので必要
);

/**
 * クリックされたらinputを表示してフォーカスする
 */
const onClickLabel = () => {
  selectInput();
  Vue.nextTick(() => {
    document.getElementById('selectable-input-container__input')?.focus();
  });
  const element = document.getElementById(selectableInputContainerId.value);
  if (!element) return;
  result.value = element.getBoundingClientRect();
  xLength.value = result.value.left;
  yLength.value = result.value.bottom;
};

/**
 * サジェストが選択された時の処理
 * @param suggest - 選択されたサジェスト
 */
const onClickSuggest = (suggest: string) => {
  updateBySelectedSuggest(suggest, props.options);
  leaveSuggests();
  confirmValue();
};
/**
 * inputのフォーカスが外れた時の処理
 */
const onBlurInput = () => {
  // マウスがsuggestに乗っている時のblurは選択肢を選んだ時なので回避(onClickSuggestが走らなくなる) || 選択肢がない時
  if (!state.isHoverSuggests || computedOptions.value.length === 0) {
    confirmValue();
  }
};
/**
 * 値を確定
 */
const confirmValue = () => {
  leaveInput();
  emits('confirmed', state.inputValue);
};
/**
 * 表示するサジェストのリスト
 */
const computedOptions = computed((): string[] => {
  return props.options.filter((option) => option.value.toString().includes(state.inputValue.toString())).map((option) => option.text);
});

// ランダムID生成
const selectableInputContainerId = ref<string>('');
onMounted(() => {
  selectableInputContainerId.value = Math.random().toString(32).substring(2);
});
</script>

<template>
  <div :id="selectableInputContainerId" class="selectable-input-container">
    <div v-show="!state.isSelectInput" class="label-container rounded-base-small">
      <div
        class="label-container__text selectable-input-container__text-box selectable-input-container__text-box--label"
        @click="onClickLabel"
      >
        {{ displayValue }}
      </div>
      <SvgLoader class="label-container__icon" :name="'arrowDown'" @click="onClickLabel" />
    </div>
    <input
      v-show="state.isSelectInput"
      id="selectable-input-container__input"
      v-model="state.inputValue"
      class="selectable-input-container__text-box selectable-input-container__text-box--input rounded-base-small"
      type="number"
      :step="STEP_OF_ADJUST_ANNOUNCE_RATE"
      :min="MIN_OF_ADJUST_ANNOUNCE_RATE"
      :max="MAX_OF_ADJUST_ANNOUNCE_RATE"
      @blur="onBlurInput"
    />
    <Teleport v-if="computedOptions.length > 0 && state.isSelectInput" to="body">
      <Suggest
        class="selectable-input-container__suggests rounded-base-small"
        :style="{
          top: `${yLength}px`,
          left: `${xLength}px`,
        }"
        :value="state.inputValue"
        :options="computedOptions"
        @click-suggest="onClickSuggest"
        @mouseover="hoverSuggests"
        @mouseleave="leaveSuggests"
      />
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.selectable-input-container {
  display: inline-block;
  position: relative;
  cursor: pointer;
  z-index: 11;
  &__text-box {
    &--input {
      width: 100%;
    }
  }
  &__suggests {
    position: absolute;
    width: 100%;
    top: 100%;
    overflow-y: scroll;
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    z-index: 1050;
  }
}

.label-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  background-color: $semanticWhite;

  &__icon {
    height: 24px;
    width: 24px;
    padding-top: 8.59px; //top指定できないので、代わり
    padding-left: 6px; //left指定できないので、代わり
  }
}
</style>
